// ABOUTME: Modal component for creating new meals with portion management
// ABOUTME: Allows users to add meal details and multiple portions with nutritional information

import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { MealData, MealPortion, DataItem, MealTemplateData } from '../types';
import { StorageManager } from '../services/storage/StorageManager';

interface AddMealModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (meal: {
        name: string;
        description: string;
        imageUrl: string;
        category: 'meal';
        timestamp: number;
        data: MealData;
    }) => void;
    editingItem?: DataItem | null;
}

const AddMealModal: React.FC<AddMealModalProps> = ({ isOpen, onClose, onSave, editingItem }) => {
    const [name, setName] = useState('');
    const [mealType, setMealType] = useState<'breakfast' | 'lunch' | 'dinner' | 'snack'>('lunch');
    const [portions, setPortions] = useState<MealPortion[]>([
        { name: '', amount: '', calories: 0, protein: 0, carbs: 0, fat: 0 }
    ]);
    const [availableTemplates, setAvailableTemplates] = useState<DataItem[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<string>('');
    const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [expandedPortions, setExpandedPortions] = useState<Set<number>>(new Set());
    const [cloneCount, setCloneCount] = useState<number>(2);
    const [mealDate, setMealDate] = useState('');
    const [mealTime, setMealTime] = useState('');
    const [isBasicInfoExpanded, setIsBasicInfoExpanded] = useState(true);

    // Load templates when modal opens
    useEffect(() => {
        if (isOpen) {
            loadTemplates();
        }
    }, [isOpen]);

    const loadTemplates = async () => {
        try {
            const provider = await StorageManager.getProvider();
            const templates = await provider.getItems(1, 50, { filterBy: 'meal_template' });
            setAvailableTemplates(templates);
        } catch (error) {
            console.error('Failed to load meal templates:', error);
        }
    };

    const handleTemplateSelection = (templateId: string) => {
        if (!templateId) {
            setSelectedTemplate('');
            return;
        }

        const template = availableTemplates.find(t => t.id.toString() === templateId);
        if (template && template.data) {
            const templateData = template.data as MealTemplateData;
            setName(template.name);
            setMealType(templateData.mealType);
            setPortions(templateData.portions.length > 0 ? [...templateData.portions] : [{ name: '', amount: '', calories: 0, protein: 0, carbs: 0, fat: 0 }]);
            setSelectedTemplate(templateId);
        }
    };

    const saveAsTemplate = async () => {
        if (!templateName.trim()) return;

        try {
            const provider = await StorageManager.getProvider();
            const totalNutrition = calculateTotalNutrition();
            const templateData: MealTemplateData = {
                portions: portions.filter(p => p.name.trim() && p.calories > 0),
                mealType,
                calories: totalNutrition.calories,
                protein: totalNutrition.protein,
                carbs: totalNutrition.carbs,
                fat: totalNutrition.fat,
                fiber: 0,
                tags: [mealType],
                description: `Template created from meal: ${name}`
            };

            await provider.addItem({
                name: templateName.trim(),
                description: templateData.description,
                imageUrl: '',
                category: 'meal_template',
                timestamp: Date.now(),
                data: templateData
            });

            setTemplateName('');
            setShowSaveAsTemplate(false);
            await loadTemplates(); // Refresh templates list
        } catch (error) {
            console.error('Failed to save meal template:', error);
        }
    };

    // Reset form when modal opens/closes or when switching between add/edit modes
    useEffect(() => {
        if (isOpen) {
            if (editingItem && editingItem.category === 'meal') {
                // Pre-populate form with existing data
                const mealData = editingItem.data as MealData;
                setName(editingItem.name);
                setMealType(mealData.mealType);
                setPortions(mealData.portions.length > 0 ? mealData.portions : [{ name: '', amount: '', calories: 0, protein: 0, carbs: 0, fat: 0 }]);
                
                // Set date and time from existing timestamp
                const date = new Date(editingItem.timestamp);
                setMealDate(date.toISOString().split('T')[0]);
                setMealTime(date.toTimeString().slice(0, 5));
            } else {
                // Reset to empty form for new meal
                setName('');
                setMealType('lunch');
                setPortions([{ name: '', amount: '', calories: 0, protein: 0, carbs: 0, fat: 0 }]);
                setSelectedTemplate('');
                setShowSaveAsTemplate(false);
                setTemplateName('');
                
                // Set current date and time for new meal
                const now = new Date();
                setMealDate(now.toISOString().split('T')[0]);
                setMealTime(now.toTimeString().slice(0, 5));
            }
            // Reset expanded states when modal opens
            setExpandedPortions(new Set());
            // Set basic info expanded state based on whether it's a new meal or editing
            setIsBasicInfoExpanded(!editingItem);
        }
    }, [isOpen, editingItem]);

    const handleAddPortion = () => {
        setPortions([...portions, { name: '', amount: '', calories: 0, protein: 0, carbs: 0, fat: 0 }]);
    };

    const handleClonePortion = (index: number) => {
        const portionToClone = portions[index];
        const newPortions = [...portions];
        
        // Create multiple clones based on cloneCount
        for (let i = 0; i < cloneCount; i++) {
            const clonedPortion = { ...portionToClone };
            newPortions.splice(index + 1 + i, 0, clonedPortion);
        }
        
        setPortions(newPortions);
    };

    const handleRemovePortion = (index: number) => {
        setPortions(portions.filter((_, i) => i !== index));
    };

    const handlePortionChange = (index: number, field: keyof MealPortion, value: any) => {
        const updatedPortions = portions.map((portion, i) => 
            i === index ? { ...portion, [field]: value } : portion
        );
        setPortions(updatedPortions);
    };

    const togglePortionExpanded = (index: number) => {
        const newExpanded = new Set(expandedPortions);
        if (newExpanded.has(index)) {
            newExpanded.delete(index);
        } else {
            newExpanded.add(index);
        }
        setExpandedPortions(newExpanded);
    };

    const calculateTotalNutrition = () => {
        const validPortions = portions.filter(p => p.name.trim() && p.calories > 0);
        return validPortions.reduce((totals, portion) => ({
            calories: totals.calories + portion.calories,
            protein: totals.protein + (portion.protein || 0),
            carbs: totals.carbs + (portion.carbs || 0),
            fat: totals.fat + (portion.fat || 0),
        }), { calories: 0, protein: 0, carbs: 0, fat: 0 });
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!name.trim()) return;

        const validPortions = portions.filter(p => p.name.trim() && p.calories > 0);
        
        if (validPortions.length === 0) return;

        const nutrition = calculateTotalNutrition();
        
        const mealData: MealData = {
            calories: nutrition.calories,
            protein: nutrition.protein,
            carbs: nutrition.carbs,
            fat: nutrition.fat,
            fiber: 0, // Could be calculated from portions if needed
            ingredients: [],
            portions: validPortions,
            mealType,
            prepTime: 0
        };

        // Combine date and time into timestamp
        const timestamp = new Date(`${mealDate}T${mealTime}`).getTime();
        
        onSave({
            name: name.trim(),
            description: '',
            imageUrl: '',
            category: 'meal',
            timestamp: timestamp,
            data: mealData
        });

        // Reset form
        setName('');
        setMealType('lunch');
        setPortions([{ name: '', amount: '', calories: 0, protein: 0, carbs: 0, fat: 0 }]);
        setSelectedTemplate('');
        setShowSaveAsTemplate(false);
        setTemplateName('');
        onClose();
    };

    const totalNutrition = calculateTotalNutrition();

    const headerActions = (
        <>
            <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-sm text-slate-300 hover:text-white transition-colors"
            >
                Cancel
            </button>
            <button
                type="submit"
                form="meal-form"
                disabled={!name.trim() || portions.filter(p => p.name.trim() && p.calories > 0).length === 0}
                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {editingItem ? "Update" : "Save"}
            </button>
        </>
    );

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={editingItem ? "Edit Meal" : "Add New Meal"} maxWidth="max-w-4xl" preventClose={true} headerActions={headerActions}>
            <form id="meal-form" onSubmit={handleSubmit} className="space-y-4">

                {/* Template Selection */}
                {!editingItem && availableTemplates.length > 0 && (
                    <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-600">
                        <label htmlFor="templateSelect" className="block text-sm font-medium text-slate-300 mb-2">
                            Start from Template (Optional)
                        </label>
                        <select
                            id="templateSelect"
                            value={selectedTemplate}
                            onChange={(e) => handleTemplateSelection(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Create from scratch</option>
                            {availableTemplates.map((template) => (
                                <option key={template.id} value={template.id.toString()}>
                                    {template.name}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* Basic Info */}
                <div className="border border-slate-600 rounded-lg bg-slate-700/30">
                    <button
                        type="button"
                        onClick={() => setIsBasicInfoExpanded(!isBasicInfoExpanded)}
                        className="w-full flex items-center justify-between p-3 text-left hover:bg-slate-700/50 transition-colors"
                    >
                        <h3 className="text-base font-medium text-white">Meal Details</h3>
                        <svg className={`w-5 h-5 text-slate-400 transition-transform ${isBasicInfoExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                    
                    {isBasicInfoExpanded && (
                        <div className="px-3 pb-3 space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="mealName" className="block text-sm font-medium text-slate-300 mb-2">
                            Meal Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            id="mealName"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g., Chicken Caesar Salad"
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="mealType" className="block text-sm font-medium text-slate-300 mb-2">
                            Meal Type <span className="text-red-500">*</span>
                        </label>
                        <select
                            id="mealType"
                            value={mealType}
                            onChange={(e) => setMealType(e.target.value as any)}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="breakfast">Breakfast</option>
                            <option value="lunch">Lunch</option>
                            <option value="dinner">Dinner</option>
                            <option value="snack">Snack</option>
                        </select>
                    </div>
                </div>

                {/* Date and Time */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="mealDate" className="block text-sm font-medium text-slate-300 mb-2">
                            Date <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="date"
                            id="mealDate"
                            value={mealDate}
                            onChange={(e) => setMealDate(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                        />
                    </div>
                    <div>
                        <label htmlFor="mealTime" className="block text-sm font-medium text-slate-300 mb-2">
                            Time <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="time"
                            id="mealTime"
                            value={mealTime}
                            onChange={(e) => setMealTime(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            required
                        />
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Portions */}
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base font-medium text-white">Portions <span className="text-red-500">*</span></h3>
                        <button
                            type="button"
                            onClick={handleAddPortion}
                            className="px-3 py-1 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                        >
                            Add Portion
                        </button>
                    </div>
                    
                    <div className="space-y-3">
                        {portions.slice().reverse().map((portion, reverseIndex) => {
                            const index = portions.length - 1 - reverseIndex;
                            const isExpanded = expandedPortions.has(index);
                            return (
                                <div key={index} className={`${isExpanded ? 'p-3' : 'p-2'} bg-slate-700/50 rounded-lg border border-slate-600`}>
                                    <div className={`flex items-center justify-between ${isExpanded ? 'mb-2' : ''}`}>
                                        <div className="flex items-center gap-2 flex-1">
                                            <button
                                                type="button"
                                                onClick={() => togglePortionExpanded(index)}
                                                className="text-slate-400 hover:text-white transition-colors"
                                                title={isExpanded ? "Collapse" : "Expand"}
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    {isExpanded ? (
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    ) : (
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    )}
                                                </svg>
                                            </button>
                                            <span className="text-xs font-medium text-slate-300">Portion {index + 1}</span>
                                            {!isExpanded && (
                                                <span className="text-xs text-slate-400 ml-2">
                                                    {portion.name || 'Unnamed portion'} 
                                                    {portion.amount && ` • ${portion.amount}`}
                                                    {portion.calories > 0 && ` • ${portion.calories} cal`}
                                                    {portion.protein > 0 && ` • ${portion.protein}g protein`}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <select
                                                value={cloneCount}
                                                onChange={(e) => setCloneCount(Number(e.target.value))}
                                                className="px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                title="Number of clones"
                                            >
                                                <option value={1}>1</option>
                                                <option value={2}>2</option>
                                                <option value={3}>3</option>
                                            </select>
                                            <button
                                                type="button"
                                                onClick={() => handleClonePortion(index)}
                                                className="text-blue-400 hover:text-blue-300"
                                                title="Clone portion"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                </svg>
                                            </button>
                                            {portions.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemovePortion(index)}
                                                    className="text-red-400 hover:text-red-300"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                
                                    {isExpanded && (
                                        <>
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-2">
                                                <div className="lg:col-span-2">
                                                    <label className="block text-xs font-medium text-slate-400 mb-1">Portion Name *</label>
                                                    <input
                                                        type="text"
                                                        value={portion.name}
                                                        onChange={(e) => handlePortionChange(index, 'name', e.target.value)}
                                                        placeholder="Portion name *"
                                                        className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-slate-400 mb-1">Amount</label>
                                                    <input
                                                        type="text"
                                                        value={portion.amount}
                                                        onChange={(e) => handlePortionChange(index, 'amount', e.target.value)}
                                                        placeholder="Amount *"
                                                        className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-slate-400 mb-1">Calories</label>
                                                    <input
                                                        type="number"
                                                        value={portion.calories || ''}
                                                        onChange={(e) => handlePortionChange(index, 'calories', Number(e.target.value) || 0)}
                                                        placeholder="Calories *"
                                                        min="0"
                                                        className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-slate-400 mb-1">Protein (g)</label>
                                                    <input
                                                        type="number"
                                                        value={portion.protein || ''}
                                                        onChange={(e) => handlePortionChange(index, 'protein', Number(e.target.value) || 0)}
                                                        placeholder="Protein (g)"
                                                        min="0"
                                                        step="0.1"
                                                        className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-slate-400 mb-1">Carbs (g)</label>
                                                    <input
                                                        type="number"
                                                        value={portion.carbs || ''}
                                                        onChange={(e) => handlePortionChange(index, 'carbs', Number(e.target.value) || 0)}
                                                        placeholder="Carbs (g)"
                                                        min="0"
                                                        step="0.1"
                                                        className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="mt-3">
                                                <label className="block text-xs font-medium text-slate-400 mb-1">Fat (g)</label>
                                                <input
                                                    type="number"
                                                    value={portion.fat || ''}
                                                    onChange={(e) => handlePortionChange(index, 'fat', Number(e.target.value) || 0)}
                                                    placeholder="Fat (g)"
                                                    min="0"
                                                    step="0.1"
                                                    className="w-full px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                                />
                                            </div>
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Nutrition Summary */}
                {totalNutrition.calories > 0 && (
                    <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-600">
                        <h4 className="text-md font-medium text-white mb-2">Total Nutrition</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                                <span className="text-slate-300">Calories:</span>
                                <span className="ml-2 text-white font-medium">{totalNutrition.calories}</span>
                            </div>
                            <div>
                                <span className="text-slate-300">Protein:</span>
                                <span className="ml-2 text-white font-medium">{totalNutrition.protein.toFixed(1)}g</span>
                            </div>
                            <div>
                                <span className="text-slate-300">Carbs:</span>
                                <span className="ml-2 text-white font-medium">{totalNutrition.carbs.toFixed(1)}g</span>
                            </div>
                            <div>
                                <span className="text-slate-300">Fat:</span>
                                <span className="ml-2 text-white font-medium">{totalNutrition.fat.toFixed(1)}g</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Save as Template */}
                {(
                    <div className="p-3 bg-slate-700/30 rounded-lg border border-slate-600">
                        <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-slate-300">Save as Template</h4>
                            <button
                                type="button"
                                onClick={() => setShowSaveAsTemplate(!showSaveAsTemplate)}
                                className="text-xs text-blue-400 hover:text-blue-300"
                            >
                                {showSaveAsTemplate ? 'Cancel' : 'Save Template'}
                            </button>
                        </div>
                        {showSaveAsTemplate && (
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={templateName}
                                    onChange={(e) => setTemplateName(e.target.value)}
                                    placeholder="Template name"
                                    className="flex-1 px-3 py-2 bg-slate-600 border border-slate-500 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                />
                                <button
                                    type="button"
                                    onClick={saveAsTemplate}
                                    disabled={!templateName.trim()}
                                    className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Save
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Template Creation for Editing */}
                {editingItem && (
                    <div className="pt-4 border-t border-slate-700">
                        <button
                            type="button"
                            onClick={() => {
                                setTemplateName(name + ' Template');
                                setShowSaveAsTemplate(true);
                            }}
                            disabled={!name.trim() || portions.filter(p => p.name.trim() && p.calories > 0).length === 0}
                            className="px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Create Template
                        </button>
                    </div>
                )}
            </form>
        </Modal>
    );
};

export default AddMealModal;